/**
 * The assembly's own QR code, and whether the world can actually find it.
 *
 * Two things a church office needs in one place: the code they print on
 * posters, invitations and the screen behind the pulpit — and an honest answer
 * to "why are we not showing up in the church finder?".
 *
 * The code never changes. Downloading it again issues nothing new, because a
 * code already printed on five hundred invitation cards is not something
 * software is allowed to quietly replace. Campaign codes sit alongside it: the
 * same destination, counted separately, so the ministry can see whether the
 * conference banner or the social post did the work.
 */
import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  QrCode, Download, Copy, Check, Plus, Trash2, Loader2, MapPin, Navigation,
  AlertTriangle, CheckCircle2, ExternalLink, ScanLine, Globe, Clock, Save,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import api, { getErrorMessage } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const CopyButton = ({ value, label = 'Copy link', testId }) => {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="outline" size="sm" className="min-h-[40px]"
      data-testid={testId}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1800);
        } catch { toast.error('Copy the link from the box instead'); }
      }}
    >
      {done ? <Check className="mr-1.5 h-4 w-4 text-emerald-600" />
            : <Copy className="mr-1.5 h-4 w-4" />}
      {done ? 'Copied' : label}
    </Button>
  );
};

const CodeCard = ({ code, onRetire }) => (
  <div className="flex flex-col gap-3 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center"
       data-testid={`qr-code-${code.code}`}>
    <img
      src={`${API}${code.image_png}?scale=6`}
      alt={`QR code ${code.code}`}
      className="h-24 w-24 self-center rounded-lg border border-slate-100 bg-white"
    />
    <div className="min-w-0 flex-1">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
        {code.label}
        {code.is_primary ? <Badge variant="outline">Main code</Badge> : null}
        <span className="font-mono text-xs text-slate-400">{code.code}</span>
      </p>
      <p className="mt-0.5 break-all text-xs text-slate-500">{code.destination}</p>
      <p className="mt-1.5 flex flex-wrap gap-3 text-xs text-slate-600">
        <span data-testid={`qr-scans-${code.code}`}>
          <strong>{code.scans}</strong> scan{code.scans === 1 ? '' : 's'}
        </span>
        <span><strong>{code.applications}</strong> application{code.applications === 1 ? '' : 's'}</span>
        <span><strong>{code.visits}</strong> visit{code.visits === 1 ? '' : 's'} planned</span>
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm" className="min-h-[40px]">
          <a href={`${API}${code.image_png}?scale=14`} download={`fwm-qr-${code.code}.png`}
             data-testid={`qr-download-png-${code.code}`}>
            <Download className="mr-1.5 h-4 w-4" />PNG
          </a>
        </Button>
        <Button asChild variant="outline" size="sm" className="min-h-[40px]">
          <a href={`${API}${code.image_svg}`} download={`fwm-qr-${code.code}.svg`}
             data-testid={`qr-download-svg-${code.code}`}>
            <Download className="mr-1.5 h-4 w-4" />SVG for print
          </a>
        </Button>
        <CopyButton value={code.url} testId={`qr-copy-${code.code}`} />
        <Button asChild variant="ghost" size="sm" className="min-h-[40px]">
          <a href={code.destination} target="_blank" rel="noopener noreferrer"
             data-testid={`qr-preview-${code.code}`}>
            <ExternalLink className="mr-1.5 h-4 w-4" />Preview
          </a>
        </Button>
        {!code.is_primary && onRetire ? (
          <Button variant="ghost" size="sm" className="min-h-[40px] text-rose-600"
                  onClick={() => onRetire(code.code)} data-testid={`qr-retire-${code.code}`}>
            <Trash2 className="mr-1.5 h-4 w-4" />Retire
          </Button>
        ) : null}
      </div>
    </div>
  </div>
);

const CHECK_LABELS = {
  is_an_assembly: 'It is a congregation, not a region',
  is_active: 'It is active',
  page_published: 'Its public page is published',
  has_position: 'It has a position on the map',
  has_address: 'It has a public address',
  has_contact: 'It has public contact details',
  has_service_times: 'It lists service times',
};

export const ChurchQrPanel = ({ churchId }) => {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const latRef = useRef(null);
  const lngRef = useRef(null);

  const codes = useQuery({
    queryKey: ['church-qr', churchId],
    queryFn: () => api.get(`/church/${churchId}/qr`).then((r) => r.data),
    enabled: !!churchId,
  });

  const listing = useQuery({
    queryKey: ['church-listing', churchId],
    queryFn: () => api.get(`/admin/churches/${churchId}/listing`).then((r) => r.data),
    enabled: !!churchId,
  });

  const addCampaign = useMutation({
    mutationFn: () => api.post(`/church/${churchId}/qr/campaigns`, { label: label.trim() }),
    onSuccess: () => {
      toast.success('Campaign code created');
      setLabel('');
      qc.invalidateQueries({ queryKey: ['church-qr', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not create that code')),
  });

  const retire = useMutation({
    mutationFn: (code) => api.delete(`/church/${churchId}/qr/campaigns/${code}`),
    onSuccess: () => {
      toast.success('Code retired');
      qc.invalidateQueries({ queryKey: ['church-qr', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not retire that code')),
  });

  const geocode = useMutation({
    mutationFn: () => api.post(`/admin/churches/${churchId}/geocode`),
    onSuccess: (r) => {
      toast.success(r.data.note || `Placed at ${r.data.matched || 'the address on file'}`);
      qc.invalidateQueries({ queryKey: ['church-listing', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not place that address')),
  });

  const setPin = useMutation({
    mutationFn: () => api.put(`/admin/churches/${churchId}/coordinates`, {
      latitude: latRef.current?.value, longitude: lngRef.current?.value,
    }),
    onSuccess: () => {
      toast.success('Position saved');
      qc.invalidateQueries({ queryKey: ['church-listing', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save that position')),
  });

  const publish = useMutation({
    mutationFn: () => api.post(`/admin/churches/${churchId}/public-page/publish`,
                              { published: true }),
    onSuccess: (r) => {
      toast.success(r.data.message || 'Published');
      qc.invalidateQueries({ queryKey: ['church-listing', churchId] });
      qc.invalidateQueries({ queryKey: ['church-qr', churchId] });
      qc.invalidateQueries({ queryKey: ['church-public-page', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not publish the page')),
  });

  const international = useQuery({
    queryKey: ['international-qr'],
    queryFn: () => api.get('/public-qr/international').then((r) => r.data),
    // Only the ministry's own office may see it; everybody else gets a 403 and
    // simply never sees the card.
    retry: false,
  });

  if (codes.isLoading) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />Loading your church code…
      </p>
    );
  }

  const data = codes.data;
  const status = listing.data;

  return (
    <div className="space-y-4" data-testid="church-qr-panel">
      <Card>
        <CardContent className="space-y-4 p-4">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
              <QrCode className="h-4 w-4 text-slate-400" />Your church QR code
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Print it on posters, invitation cards and the screen. Scanning it opens
              this church's welcome page — find out more, plan a visit, or become a
              member. The code never changes, even if the church is renamed.
            </p>
          </div>

          {data?.primary ? <CodeCard code={data.primary} /> : null}

          <div className="space-y-2 border-t border-slate-100 pt-4">
            <Label className="text-xs">Campaign codes</Label>
            <p className="text-xs text-slate-500">
              A separate code for a conference, a poster or a social post — same
              welcome page, counted on its own.
            </p>
            {data?.campaigns?.length ? (
              <div className="space-y-2">
                {data.campaigns.map((c) => (
                  <CodeCard key={c.code} code={c} onRetire={(code) => retire.mutate(code)} />
                ))}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Easter outreach poster"
                className="max-w-xs"
                data-testid="qr-campaign-label"
              />
              <Button
                variant="outline"
                disabled={label.trim().length < 2 || addCampaign.isPending}
                onClick={() => addCampaign.mutate()}
                data-testid="qr-campaign-add"
              >
                {addCampaign.isPending
                  ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  : <Plus className="mr-1.5 h-4 w-4" />}
                Add a campaign code
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
              <ScanLine className="h-4 w-4 text-slate-400" />Being found
            </h3>            <p className="mt-1 text-sm text-slate-500">
              Whether a stranger searching the church finder can see this assembly.
            </p>
          </div>

          {status ? (
            <>
              {status.listed ? (
                <p className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-sm text-emerald-900"
                   data-testid="listing-ok">
                  <CheckCircle2 className="h-4 w-4" />
                  Listed in the church finder.
                </p>
              ) : (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-900"
                     data-testid="listing-blocked">
                  <p className="flex items-center gap-2 font-semibold">
                    <AlertTriangle className="h-4 w-4" />Not yet listed in the church finder
                  </p>
                  <p className="mt-1">
                    {status.blocking.includes('page_published')
                      ? 'Publishing its public page makes it findable straight away.'
                      : status.blocking.includes('is_an_assembly')
                        ? 'This record is a region rather than a congregation, so nobody can attend it.'
                        : 'This church is marked inactive.'}
                  </p>
                  {status.blocking.includes('page_published') ? (
                    <Button
                      size="sm"
                      className="mt-2 min-h-[44px] bg-amber-600 hover:bg-amber-700"
                      disabled={publish.isPending}
                      onClick={() => publish.mutate()}
                      data-testid="listing-publish"
                    >
                      {publish.isPending
                        ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        : <Globe className="mr-1.5 h-4 w-4" />}
                      Publish this page now
                    </Button>
                  ) : null}
                </div>
              )}

              <ul className="grid gap-1.5 sm:grid-cols-2" data-testid="listing-checks">
                {Object.entries(status.checks).map(([key, ok]) => (
                  <li key={key} className="flex items-center gap-2 text-sm">
                    {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                    <span className={ok ? 'text-slate-600' : 'text-slate-500'}>
                      {CHECK_LABELS[key] || key}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2 border-t border-slate-100 pt-4">
                <Label className="flex items-center gap-1.5 text-xs">
                  <MapPin className="h-3.5 w-3.5" />Position on the map
                </Label>
                <p className="text-xs text-slate-500">
                  Needed for "find churches near me". We place it automatically
                  when the address is saved — {status.address_used_for_lookup || 'no address yet'}.
                </p>
                {status.position_note ? (
                  <p className="text-xs text-amber-700" data-testid="listing-position-note">
                    {status.position_note}
                  </p>
                ) : status.checks.has_position && status.position_source ? (
                  <p className="text-xs text-emerald-700" data-testid="listing-position-source">
                    Placed from {status.position_source === 'set by hand'
                      ? 'a position set by hand'
                      : `“${status.position_source}”`}
                    {status.position_precision && status.position_precision !== 'address'
                      ? ` · accurate to the ${status.position_precision}`
                      : ''}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-end gap-2">
                  <Button
                    variant="outline"
                    disabled={geocode.isPending || !status.checks.has_address}
                    onClick={() => geocode.mutate()}
                    data-testid="listing-geocode"
                  >
                    {geocode.isPending
                      ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      : <Navigation className="mr-1.5 h-4 w-4" />}
                    Work it out from the address
                  </Button>
                  <div>
                    <Label className="text-[11px] text-slate-500">Latitude</Label>
                    <Input ref={latRef} defaultValue={status.latitude ?? ''}
                           className="w-32" placeholder="-17.8252" data-testid="listing-lat" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Longitude</Label>
                    <Input ref={lngRef} defaultValue={status.longitude ?? ''}
                           className="w-32" placeholder="31.0335" data-testid="listing-lng" />
                  </div>
                  <Button variant="outline" disabled={setPin.isPending}
                          onClick={() => setPin.mutate()} data-testid="listing-save-pin">
                    Save position
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">Checking…</p>
          )}
        </CardContent>
      </Card>

      {international.data?.primary ? (
        <Card data-testid="international-qr-card">
          <CardContent className="space-y-3 p-4">
            <div>
              <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
                <QrCode className="h-4 w-4 text-slate-400" />The ministry's international code
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                One code for conferences, outreach and printed invitations anywhere in
                the world. It opens Find Your Church rather than any one assembly, so
                whoever scans it can choose the church nearest them.
              </p>
            </div>
            <CodeCard code={international.data.primary} />
            {international.data.campaigns?.length ? (
              <div className="space-y-2">
                {international.data.campaigns.map((c) => (
                  <CodeCard key={c.code} code={c} />
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default ChurchQrPanel;
